<script lang="ts">
  import { getContext } from "svelte"
  import { xhr, toast } from "$app/utils"
  import Tags from "svelte-tags-input"
  import Switch from "$app/components/Switch.svelte"

  let props = $props()
  let book: BookType = $state(props.book || {})
  let status = $state(props.status)

  const categories = getContext("categories") as string[]
  const values = getContext("values") as string[]
  const statuses = getContext("statuses") as string[]

  const fields = [
    "title",
    "author",
    "description",
    "public",
    "status",
    "publication_date",
    "affiliate_link",
    "categories",
    "values"
  ]

  let input: HTMLInputElement
  let file: any
  let showImage = $state(false)
  let image: any = $state(null)

  let form = {} as BookType

  let errors: any = $state({})
  let saving = $state(false)

  async function saveBook() {
    if (status === "edit") {
      await updateBook()
    } else {
      await createBook()
    }
  }

  function getFormData(): FormData {
    const formData = new FormData()
    fields.forEach((field) => {
      if (["categories", "values"].includes(field)) {
        form[field].forEach((value) => {
          formData.append(`book[${field}][]`, value)
        })
      } else {
        formData.append(`book[${field}]`, form[field] || "")
      }
    })

    if (showImage) {
      formData.append("book[image]", file)
    }
    return formData
  }

  function validForm(): boolean {
    errors = {}

    if (!showImage && status === "new") {
      errors.image = "Invalid image"
    }
    return Object.keys(errors).length === 0
  }

  async function updateBook(): Promise<void> {
    if (!validForm()) return

    try {
      saving = true
      const resp = await xhr.patch(
        `/admin/books/${book.id}.json`,
        getFormData()
      )

      toast("Book has been updated", "success")
      onupdate(resp.data)
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      saving = false
    }
  }

  async function createBook(): Promise<void> {
    if (!validForm()) return
    try {
      saving = true
      const resp = await xhr.post(`/admin/books.json`, getFormData())

      toast("Book has been created", "success")
      oncreate(resp.data)
    } catch (e) {
      console.error(e)
      errors = e.response.data.errors
    } finally {
      saving = false
    }
  }

  function setForm(): void {
    fields.forEach((field) => {
      if (["categories", "values"].includes(field)) {
        form[field] = book[field] || []
      } else {
        form[field] = book[field]
      }
    })
  }

  function onChangeImage(): void {
    file = input.files[0]

    if (file) {
      showImage = true

      const reader = new FileReader()
      reader.addEventListener("load", function () {
        image.setAttribute("src", reader.result)
      })
      reader.readAsDataURL(file)

      return
    }
    showImage = false
  }

  if (status) {
    showImage = false
    setForm()
  }
</script>

<form>
  <h2 class="text-base font-semibold leading-6 text-gray-900">
    {status === "edit" ? "Edit" : "New"} Book
  </h2>

  <div class="flex mt-2">
    <div>
      <!-- svelte-ignore a11y_click_events_have_key_events -->
      <div
        onclick={() => input.click()}
        class="cursor-pointer border-2"
        role="button"
        tabindex="0"
      >
        <div class="mx-auto img-cont">
          {#if showImage}
            <img bind:this={image} src="" alt="Preview" width="200" />
          {:else if status === "edit"}
            <img src={book.image_url} alt={book.title} width="200" />
          {:else}
            <div
              class="h-full p-4 text-xl text-gray-400 flex items-center hover:bg-gray-600"
            >
              Select an Image
            </div>
          {/if}
        </div>
      </div>

      <div class="text-red-500">
        {errors.image || ""}
      </div>
      <input
        bind:this={input}
        onchange={onChangeImage}
        type="file"
        accept=".jpg, .jpeg, .png, .webp"
        class="hidden"
      />
    </div>

    <div class="w-full px-2">
      <div class="input-cont">
        <label class="!pt-0 !mt-0" for="">Title</label>
        <input
          class="input-admin input w-full"
          type="text"
          bind:value={form.title}
        />
        <div class="text-red-500">
          {errors.title || ""}
        </div>
      </div>

      <div class="input-cont">
        <label for="">Author</label>
        <input
          class="input-admin input w-full"
          type="text"
          bind:value={form.author}
        />
        <div class="text-red-500">
          {errors.author || ""}
        </div>
      </div>

      <div class="input-cont">
        <label for="">Description</label>
        <textarea
          class="input-admin input w-full"
          rows="4"
          bind:value={form.description}
        ></textarea>
        <div class="text-red-500">
          {errors.description || ""}
        </div>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-3 gap-4 items-center">
    <div class="input-cont">
      <label for="book-public" class="flex items-center gap-1 cursor-pointer">
        <Switch active={form.public} --ratio="0.8" id="book-public" />
        Public
      </label>
      <div class="text-red-500">
        {errors.public || ""}
      </div>
    </div>

    <div class="input-cont">
      <label for="">Publication Date</label>
      <input
        class="input-admin w-full input"
        type="date"
        bind:value={form.publication_date}
      />
    </div>

    <div class="input-cont">
      <label for="">Status</label>
      <select class="input-admin" bind:value={form.status}>
        {#each statuses as status}
          <option value={status}>{status}</option>
        {/each}
      </select>
    </div>
  </div>

  <div class="input-cont">
    <label for="">Categories</label>
    <div class="svelte-tags">
      <Tags
        bind:tags={form.categories}
        autoComplete={categories}
        addKeys={[]}
        minChars={0}
        removeKeys={[8]}
        onlyUnique={true}
      />
    </div>
  </div>

  <div class="input-cont">
    <label for="">Values</label>
    <div class="svelte-tags">
      <Tags
        bind:tags={form.values}
        autoComplete={values}
        minChars={0}
        addKeys={[]}
        removeKeys={[8]}
        onlyUnique={true}
      />
    </div>

    <div class="text-red-500">
      {errors.description || ""}
    </div>
  </div>

  <div class="input-cont">
    <label for="">Affiliate link</label>
    <input class="w-full input" type="text" bind:value={form.affiliate_link} />
  </div>

  <div class="p-4 text-center">
    <button class="btn big primary" disabled={saving} onclick={saveBook}>
      {status === "edit" ? "Update" : "Create"}
    </button>
  </div>
</form>

<style lang="postcss">
  .img-cont {
    width: 200px;
    height: 300px;
    overflow: hidden;
  }
  .img-cont * {
    display: block;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    justify-content: center;
  }
  .img-cont img {
    object-fit: cover;
    height: 100%;
  }
</style>
